"use client";
import { useContext, useLayoutEffect, useState } from "react";
import { CornerLayer, type Corner } from "./corner-context";
import s from "./corners.module.css";

function placeItem({
  item,
  stack,
  order,
}: {
  readonly item: HTMLElement;
  readonly stack: HTMLElement;
  readonly order: number;
}) {
  item.className = s.item;
  item.style.order = String(order);

  if (item.parentElement === stack) return;

  const active = document.activeElement;
  const focused =
    active instanceof HTMLElement && item.contains(active) ? active : null;
  stack.append(item);
  focused?.focus({ preventScroll: true });
}

/** Keep one portal target while moving it, preserving content state and focus. */
export function useCornerItem({
  corner,
  order,
}: {
  readonly corner: Corner;
  readonly order: number;
}) {
  const layer = useContext(CornerLayer);
  const [item] = useState(() =>
    typeof document === "undefined" ? null : document.createElement("div"),
  );
  useLayoutEffect(() => {
    if (!layer || !item) return;

    const stack = layer.querySelector<HTMLElement>(
      `:scope > [data-band] > [data-corner="${corner}"]`,
    );

    if (!stack) throw new Error(`Missing site corner: ${corner}`);

    placeItem({ item, stack, order });
  }, [layer, corner, order, item]);
  useLayoutEffect(() => () => item?.remove(), [item]);
  return layer ? item : null;
}
